import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { formatApiError } from "@/lib/api";
import { trackMetaEvent } from "@/lib/meta";
import { INTENT_OPTIONS } from "@/lib/membership";

const inputCls =
  "w-full bg-transparent border-b border-ptf-primary/25 py-3 text-base outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

function GoogleButton() {
  const { loginWithGoogle } = useMemberAuth();
  return (
    <button
      type="button"
      data-testid="member-google-btn"
      onClick={loginWithGoogle}
      className="w-full flex items-center justify-center gap-3 border border-ptf-primary/20 bg-white py-3.5 text-sm font-medium text-ptf-primary hover:border-ptf-primary/50 transition-colors"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      Continue with Google
    </button>
  );
}

export function LoginForm({ onSwitch }) {
  const { login } = useMemberAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email.trim().toLowerCase(), form.password);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not log in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <GoogleButton />
      <div className="flex items-center gap-3 text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/40">
        <span className="h-px flex-1 bg-ptf-primary/12" />or<span className="h-px flex-1 bg-ptf-primary/12" />
      </div>
      <form onSubmit={submit} data-testid="member-login-form" className="space-y-5">
        <div>
          <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Email *</label>
          <input required type="email" data-testid="login-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
        </div>
        <div>
          <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Password *</label>
          <input required type="password" data-testid="login-password" value={form.password} onChange={set("password")} className={inputCls} placeholder="••••••••" />
        </div>
        <button type="submit" data-testid="login-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-ptf-primary text-ptf-bg px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Signing in" : "Log In"}
        </button>
      </form>
      <p className="text-sm text-ptf-text/60">
        New to Planet Trotter?{" "}
        <button type="button" data-testid="switch-to-register" onClick={onSwitch} className="text-ptf-primary underline underline-offset-4 hover:text-ptf-secondary">Create a member account</button>
      </p>
    </div>
  );
}

export function RegisterForm({ onSwitch, requireIntent = true }) {
  const { register } = useMemberAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", intent: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (requireIntent && !form.intent) { toast.error("Please tell us what you'd like to do."); return; }
    setLoading(true);
    try {
      await register(form);
      trackMetaEvent("CompleteRegistration", { content_name: "Member Account", status: form.intent || "DAWN" });
      toast.success("Account created. Let's complete your profile.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <GoogleButton />
      <div className="flex items-center gap-3 text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/40">
        <span className="h-px flex-1 bg-ptf-primary/12" />or<span className="h-px flex-1 bg-ptf-primary/12" />
      </div>
      <form onSubmit={submit} data-testid="member-register-form" className="space-y-5">
        <div>
          <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Full Name *</label>
          <input required data-testid="register-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
        </div>
        <div>
          <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Email *</label>
          <input required type="email" data-testid="register-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
        </div>
        <div>
          <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Password * (min. 8 characters)</label>
          <input required type="password" minLength={8} data-testid="register-password" value={form.password} onChange={set("password")} className={inputCls} placeholder="••••••••" />
        </div>
        {requireIntent && (
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">I want to: *</label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {INTENT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`register-intent-${opt.value.toLowerCase()}`}
                  onClick={() => setForm((f) => ({ ...f, intent: opt.value }))}
                  className={`border px-3 py-2.5 text-xs uppercase tracking-[0.1em] transition-colors ${form.intent === opt.value ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary/50"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <button type="submit" data-testid="register-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-ptf-primary text-ptf-bg px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Creating account" : "Create Account"}
        </button>
      </form>
      <p className="text-sm text-ptf-text/60">
        Already a member?{" "}
        <button type="button" data-testid="switch-to-login" onClick={onSwitch} className="text-ptf-primary underline underline-offset-4 hover:text-ptf-secondary">Log in</button>
      </p>
    </div>
  );
}

export default function AuthForms({ requireIntent = true }) {
  const [mode, setMode] = useState("register");
  return (
    <div className="bg-white border border-ptf-primary/10 p-7 md:p-10 max-w-lg">
      <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50 mb-1">Member Account</p>
      <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary mb-6">{mode === "login" ? "Log In" : "Join Planet Trotter"}</h2>
      {mode === "login" ? <LoginForm onSwitch={() => setMode("register")} /> : <RegisterForm onSwitch={() => setMode("login")} requireIntent={requireIntent} />}
    </div>
  );
}
